import type { LDContext } from "launchdarkly-js-client-sdk";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";

import { isWithinIframe } from "metabase/lib/dom";

import LoadingSpinner from "../../../../components/LoadingSpinner";
import { config } from "../../config";
import { useRetrieveLDContext } from "../use-retrieve-ld-context";

interface LaunchDarklyProviderFunction {
  provider: ({ children }: { children: ReactNode }) => JSX.Element;
}

export const LaunchDarklyProvider: FC = ({ children: childComponents }) => {
  // LaunchDarkly flags are tied to a given user, so we'll need to make sure we'll have a valid session (companyId)
  // and user available when we set up our LD session; otherwise, we'd wind up in anonymous mode or would
  // need to load a new LD configuration on the fly.
  const [LDProvider, setLDProvider] =
    useState<LaunchDarklyProviderFunction | null>(null);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const ldContextFromPortal = useRetrieveLDContext();

  /**
   * Setup LD provider
   */
  useEffect(() => {
    if (LDProvider || !ldContextFromPortal) {
      return;
    }

    // LD returns its provider via a Promise, which we'll await so we ensure that
    // flags have loaded before our application loads; otherwise, we'd see flags "pop in" and
    // change the UI after it has already rendered.
    (async () => {
      const multiContext: LDContext = ldContextFromPortal;
      try {
        const ldProviderFunction = await asyncWithLDProvider({
          clientSideID: config.tokens.launchDarkly,
          context: multiContext,
          options: {
            streaming: true,
          },
        });
        setLDProvider({ provider: ldProviderFunction });
      } catch (e) {
        setHasLoadFailed(true);
      }
    })();
  }, [LDProvider, ldContextFromPortal]);

  if (!isWithinIframe()) {
    return <>{childComponents}</>;
  }

  // Show loading spinner while we wait for our hooks to load and the async effect to fetch our LD Provider.
  if (!LDProvider && !hasLoadFailed) {
    return <LoadingSpinner />;
  }

  // The async function has loaded our LD Provider component; render it and all children.
  // Children now have access to useFlags() LD hook.
  if (LDProvider && LDProvider.provider) {
    return <LDProvider.provider>{childComponents}</LDProvider.provider>;
  }

  // If loading flags has failed and there is no provider, return all children and
  // continue on rather than serving a loading indicator indefinitely.
  return <>{childComponents}</>;
};
